import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Spin, Tabs } from 'antd';
import toast from 'react-hot-toast';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import dashboardService from '../../../../services/dashboardService';
import { downloadAdminPreIpoDealSheet } from '../../../../services/adminPreIpoReportsService';
import ReportingScopeToolbar from './components/ReportingScopeToolbar';
import {
  filenameFromContentDisposition,
  getDefaultReportDateRange,
  isJsonBlob,
  readBlobErrorMessage,
  toReportQueryParams,
  triggerBlobDownload,
} from './commons/preIpoReportingFormat.helpers';

const OverviewTab = lazy(() => import('./components/OverviewTab'));
const PipelineTab = lazy(() => import('./components/PipelineTab'));
const ProductMixTab = lazy(() => import('./components/ProductMixTab'));
const PartnerPerformanceTab = lazy(() => import('./components/PartnerPerformanceTab'));

function TabPanel({ children }) {
  return <Suspense fallback={<Spin className="py-12" />}>{children}</Suspense>;
}

export default function PreIpoReportingPage() {
  const [dateRange, setDateRange] = useState(getDefaultReportDateRange);
  const [scopeUserId, setScopeUserId] = useState('all');
  const [scopeOptions, setScopeOptions] = useState([{ id: 'all', label: 'All' }]);
  const [viewerScope, setViewerScope] = useState('team');
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [downloadingDealSheet, setDownloadingDealSheet] = useState(false);

  const queryParams = useMemo(
    () => toReportQueryParams(dateRange, scopeUserId),
    [dateRange, scopeUserId],
  );

  const loadScopeOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const payload = await dashboardService.getScopeOptions();
      setScopeOptions(payload?.options?.length ? payload.options : [{ id: 'all', label: 'All' }]);
      setViewerScope(payload?.viewer_scope ?? 'team');
    } catch {
      setScopeOptions([{ id: 'all', label: 'All' }]);
      setViewerScope('team');
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScopeOptions();
  }, [loadScopeOptions]);

  const handleDownloadDealSheet = useCallback(async () => {
    if (!queryParams?.startDate || !queryParams?.endDate) {
      toast.error('Select a valid date range.');
      return;
    }
    if (downloadingDealSheet) return;

    setDownloadingDealSheet(true);
    try {
      const response = await downloadAdminPreIpoDealSheet(queryParams);
      const blob = response?.data;
      const contentType = String(response?.headers?.['content-type'] || blob?.type || '');

      if (isJsonBlob(blob, contentType)) {
        const text = await blob.text();
        let message = 'Download failed';
        try {
          message = JSON.parse(text).message || message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      const filename = filenameFromContentDisposition(
        response?.headers?.['content-disposition'],
        `deal-sheets-${queryParams.startDate}-to-${queryParams.endDate}.zip`,
      );
      triggerBlobDownload(blob, filename);
      toast.success('Deal sheet downloaded.');
    } catch (error) {
      const message = await readBlobErrorMessage(error, error?.message || 'Failed to download deal sheet.');
      toast.error(message);
    } finally {
      setDownloadingDealSheet(false);
    }
  }, [downloadingDealSheet, queryParams]);

  const tabItems = [
    {
      key: 'overview',
      label: 'Business overview',
      children: (
        <TabPanel>
          <OverviewTab queryParams={queryParams} />
        </TabPanel>
      ),
    },
    {
      key: 'pipeline',
      label: 'Order pipeline',
      children: (
        <TabPanel>
          <PipelineTab queryParams={queryParams} />
        </TabPanel>
      ),
    },
    {
      key: 'product-mix',
      label: 'Product mix',
      children: (
        <TabPanel>
          <ProductMixTab queryParams={queryParams} />
        </TabPanel>
      ),
    },
    {
      key: 'partners',
      label: 'Partner performance',
      children: (
        <TabPanel>
          <PartnerPerformanceTab queryParams={queryParams} />
        </TabPanel>
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Pre-IPO Reporting</h1>
            <p className="mt-1 text-sm text-gray-500">
              {viewerScope === 'platform'
                ? 'Platform-wide analytics for the selected period.'
                : 'Analytics for you and your team (downline RMs and their partner books).'}
            </p>
          </div>
          <ReportingScopeToolbar
            dateRange={dateRange}
            onDateRangeChange={(range) => {
              if (range?.[0] && range?.[1]) setDateRange(range);
            }}
            scopeUserId={scopeUserId}
            onScopeChange={setScopeUserId}
            scopeOptions={scopeOptions}
            optionsLoading={optionsLoading}
            downloadingDealSheet={downloadingDealSheet}
            onDownloadDealSheet={handleDownloadDealSheet}
          />
        </div>
        <Tabs defaultActiveKey="overview" items={tabItems} destroyOnHidden />
      </div>
    </DashboardPageWrapper>
  );
}
